import { useState, useEffect } from 'react';
import { useAppStore } from '@/store/appStore';
import { useSpaces } from './useSpaces';
import { useViewData } from './useViewData';
import { getSavedFilters, type SavedFilter } from '@/db/savedFilters';
import { handleError } from '@/utils/errorHandler';

/**
* Hook per la Command Palette.
* Combina useViewData e useSpaces per fornire tutti i dati necessari alla command palette.
* Gestisce la selezione dei task e la navigazione.
*
* @returns Oggetto con lo stato e le funzioni della command palette
* @returns isOpen - Se la command palette è aperta
* @returns onClose - Funzione per chiudere la command palette
* @returns tasks - Task arricchiti disponibili per la ricerca
* @returns spaces - Spaces disponibili per la navigazione
* @returns savedFilters - Filtri salvati disponibili per l'applicazione rapida
* @returns onSelectTask - Funzione per selezionare un task
* @returns onNavigate - Funzione per navigare a space/folder/list
* @returns onViewChange - Funzione per cambiare la vista
* @returns onApplyFilter - Funzione per applicare un filtro salvato
*/
export function useCommandPalette() {
  const { spaces } = useSpaces();
  const viewData = useViewData();

  const isOpen = useAppStore((state) => state.isCommandPaletteOpen);
  const closeCommandPalette = useAppStore((state) => state.closeCommandPalette);
  const selectTask = useAppStore((state) => state.selectTask);
  const setFilters = useAppStore((state) => state.setFilters);
  const setGrouping = useAppStore((state) => state.setGrouping);
  const setSorting = useAppStore((state) => state.setSorting);

  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);

  useEffect(() => {
    getSavedFilters('current-user')
      .then(setSavedFilters)
      .catch((err) => handleError(err, { level: 'warning', message: 'Errore nel caricamento dei filtri salvati' }));
  }, []);

  const handleSelectTask = (task: import('../types').Task) => {
    selectTask(task.id);
  };

  const handleApplyFilter = (filter: SavedFilter) => {
    setFilters(filter.filters);
    if (filter.grouping) {
      setGrouping(filter.grouping);
    }
    if (filter.sorting) {
      setSorting(filter.sorting);
    }
  };

  return {
    isOpen,
    onClose: closeCommandPalette,
    tasks: viewData.enrichedTasks,
    spaces,
    savedFilters,
    onSelectTask: handleSelectTask,
    onNavigate: viewData.onNavigate,
    onViewChange: viewData.onViewModeChange,
    onApplyFilter: handleApplyFilter,
  };
}

